import { isEqual } from 'lodash-es';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@shared/components/AuthProvider';
import { AnalyticsContext } from '@shared/core/analytics';
import { createContext } from '@shared/utils/createContext';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

import type { FeatureFlags } from '../featureFlags.constants';
import { useFeatureFlagsContext } from '../featureFlags.provider';
import type { RemoteFeatureFlagKeys, RemoteFeatureFlagOptions } from '../featureFlags.types';
import { createOneTimePerfLogger, createPerfLogger, logEvent, parseError, usePerfLogger } from './remoteFeatureFlags.logger';
import type { RemoteFeatureFlag, RemoteFeatureFlagFetchStatus, RemoteFeatureFlagService, UserInfo } from './remoteFeatureFlags.types';
import { useAmplitudeExperiment } from './useAmplitudeExperiment';
import type { BuildExperimentUserInput } from './amplitude.utils';
import { buildExperimentUser } from './amplitude.utils';
import { getLocalStorage } from '@shared/core/storage';
import { useUserConsent } from '@shared/utils/userConsent';
import globalWindow from '@shared/core/globals';
import type { Variants } from '@amplitude/experiment-js-client';

export interface InternalRemoteFeatureFlagsContextType {
  loading: boolean;
  /**
   * The outcome of this session's own `fetchUser`. `loading` cannot answer this: a failed fetch is
   * logged and still transitions to Ready on the variants the client already held, and SSR variants
   * start out Ready with no client fetch attempted at all. Only `requireFreshFetch` callers read it.
   */
  fetchStatus: RemoteFeatureFlagFetchStatus;
  getFeatureFlagValue: <TFeatureName extends RemoteFeatureFlagKeys>(
    key: TFeatureName,
    options?: RemoteFeatureFlagOptions,
  ) => { value?: FeatureFlags[TFeatureName]['supportedValues'][number]; payload?: unknown };
}

const [InternalRemoteFeatureFlagsProvider, useInternalRemoteFeatureFlagsContext, InternalRemoteFeatureFlagsContext] = createContext<InternalRemoteFeatureFlagsContextType>({
  name: 'RemoteFeatureFlagProvider',
});

enum RemoteFeatureFlagProviderState {
  Loading,
  Ready,
}

const logFirstLoadPerf = createOneTimePerfLogger('FirstLoad');

/**
 * Who the variants on hand belong to: the identity fields of the fetched profile, and nothing else.
 * A fetch status is only meaningful for the identity it was fetched for, and the identity changes
 * during a render (a sign-in, a different event) while the fetch that answers for it is still an
 * effect away — so `requireFreshFetch` callers must see `pending` from that render on, not one more
 * render of the previous user's `success`.
 *
 * The profile's `userProperties` are deliberately not part of this: they are targeting inputs, not
 * identity, and the effect's `isEqual` guard still refetches when one of them changes.
 */
const toProfileIdentity = (profile: Readonly<{ authUserId?: string; anonymousId?: string; eventId?: string; eventFirebaseId?: string }>) =>
  [profile.authUserId, profile.anonymousId, profile.eventId, profile.eventFirebaseId].map(field => field ?? '').join('|');

function isSupportedFeatureValue<TFeatureName extends RemoteFeatureFlagKeys>(
  value: string,
  supportedValues: FeatureFlags[TFeatureName]['supportedValues'],
): value is FeatureFlags[TFeatureName]['supportedValues'][number] {
  return Array.isArray(supportedValues) && (supportedValues as string[]).includes(value.toLowerCase());
}

const getForceHotelBlockEnabled = () => {
  const localStorage = getLocalStorage();
  const ajsUserTraits = localStorage.getItem('ajs_user_traits') || '';
  let isForceHotelBlockEnabled = false;
  if (ajsUserTraits) {
    try {
      const ajsUserTraitsObject = JSON.parse(ajsUserTraits);
      if (ajsUserTraitsObject?.forceHotelBlockEnabled) {
        isForceHotelBlockEnabled = ajsUserTraitsObject.forceHotelBlockEnabled === true;
        logEvent('GetForceHotelBlockEnabled', { status: 'success' });
      }
    } catch (err) {
      logEvent('GetForceHotelBlockEnabled', { status: 'error', error: parseError(err) });
    }
  }
  return isForceHotelBlockEnabled;
};

export interface RemoteFeatureFlagProviderParams {
  remoteFeatureFlagService?: RemoteFeatureFlagService;
  initialAmplitudeFeatureFlagVariants?: Variants;
}

export const RemoteFeatureFlagsProvider: React.FC<RemoteFeatureFlagProviderParams> = ({ children, remoteFeatureFlagService, initialAmplitudeFeatureFlagVariants }) => {
  /**
   * Check for window.__INITIAL_AMPLITUDE_FLAGS__ BEFORE calling useAmplitudeExperiment
   * because the hook will delete the global after reading it.
   *
   * On server: initialAmplitudeFeatureFlagVariants prop contains SSR-fetched variants
   * On client: window.__INITIAL_AMPLITUDE_FLAGS__ contains SSR-embedded variants (prop is undefined)
   */
  const hadWindowGlobalOnInit = useRef(!!globalWindow.__INITIAL_AMPLITUDE_FLAGS__);

  const amplitudeService = useAmplitudeExperiment(initialAmplitudeFeatureFlagVariants);
  const featureFlagService = remoteFeatureFlagService ?? amplitudeService;

  // Start in Ready state if we have SSR-fetched variants (from either prop or window global)
  const hasInitialVariants = initialAmplitudeFeatureFlagVariants || hadWindowGlobalOnInit.current;
  const initialState = hasInitialVariants ? RemoteFeatureFlagProviderState.Ready : RemoteFeatureFlagProviderState.Loading;
  const [state, setState] = useState<RemoteFeatureFlagProviderState>(initialState);

  const auth = useAuth();
  const authUserId = auth.currentUser.profile?.activeAlias?.auth0Id;
  const authHasInitialized = auth.hasInitializedOnce;
  usePerfLogger({ name: 'Dependency_Auth', isLoading: !authHasInitialized });

  const { anonymousId } = useContext(AnalyticsContext);
  usePerfLogger({ name: 'Dependency_SegmentUser', isLoading: !anonymousId });

  const { loading: eventInfoLoading, eventInfo } = useEventInfo();
  usePerfLogger({ name: 'Dependency_EventInfo', isLoading: eventInfoLoading });

  const { getUserConsent, loadingUserConsent } = useUserConsent();
  usePerfLogger({ name: 'Dependency_UserConsent', isLoading: loadingUserConsent });

  // `identity: null` is "nothing fetched yet", which no real identity can spell.
  const [fetchOutcome, setFetchOutcome] = useState<{ identity: string | null; status: RemoteFeatureFlagFetchStatus }>({ identity: null, status: 'pending' });
  const profileIdentity = toProfileIdentity({ authUserId, anonymousId, eventId: eventInfo?.eventId, eventFirebaseId: eventInfo?.eventFirebaseId });
  // Derived during render, so a profile that changed this render is already stale by the time any
  // consumer reads it — rather than one render later, when the effect gets to run.
  const fetchStatus: RemoteFeatureFlagFetchStatus = fetchOutcome.identity === profileIdentity ? fetchOutcome.status : 'pending';
  const lastFetchedUserProfile = useRef<UserInfo>({});
  const fetchIdRef = useRef(0);
  /**
   * Counts fetches actually issued, where `fetchIdRef` counts effect runs — most of which issue no
   * fetch at all (the `isEqual` guard below). The status has to be owned by the attempt that set it
   * in flight: guarding it with `fetchIdRef` instead would let a later effect run that fetched
   * nothing orphan the in-flight attempt's result, leaving the status `pending` for good.
   */
  const fetchAttemptIdRef = useRef(0);

  useEffect(() => {
    // Skip the loading state if we have SSR variants until dependencies are ready
    // This prevents returning default values during SSR when we already have correct values
    if (!anonymousId || !authHasInitialized || eventInfoLoading || loadingUserConsent) {
      return;
    }

    // Increment fetch ID to track the latest request; stale completions are ignored
    const currentFetchId = ++fetchIdRef.current;

    const fetchRemoteFeatureFlags = async () => {
      const forceHotelBlockEnabled = getForceHotelBlockEnabled();
      const createdAtMs = eventInfo?.firebaseCreateAt?.timestamp ? new Date(eventInfo.firebaseCreateAt.timestamp).getTime() : Number.NaN;
      const eventCreatedAtMs = Number.isFinite(createdAtMs) ? createdAtMs : undefined;

      const userInfoToFetch: BuildExperimentUserInput = {
        userId: authUserId,
        deviceId: anonymousId,
        eventFirebaseId: eventInfo?.eventFirebaseId,
        eventId: eventInfo?.eventId,
        userProperties: {
          forceHotelBlockEnabled,
          // Lets Amplitude target "events created since <date>" without a deploy — the "Get started"
          // tour is the first consumer, gated to new events only. Sent as epoch ms because that's
          // what Amplitude's numeric `greater than` operator can compare; the ISO copy alongside it
          // is for whoever reads the segment later and needs the number to mean something.
          ...(eventCreatedAtMs !== undefined ? { eventCreatedAtMs, eventCreatedAt: new Date(eventCreatedAtMs).toISOString() } : {}),
        },
      };
      if (!isEqual(lastFetchedUserProfile.current, userInfoToFetch)) {
        lastFetchedUserProfile.current = userInfoToFetch;

        const attemptId = ++fetchAttemptIdRef.current;
        const fetchedIdentity = toProfileIdentity({ authUserId, anonymousId, eventId: eventInfo?.eventId, eventFirebaseId: eventInfo?.eventFirebaseId });
        // The variants on hand belong to the profile that is being replaced — a logged-out session's,
        // when this fetch is the one that follows a login. Fresh for the old user is stale for the
        // new one, so `requireFreshFetch` callers go back to loading until this attempt settles.
        // (An identity change is already `pending` from the render that made it; this covers a
        // refetch that keeps the identity and changes only a targeting property.)
        setFetchOutcome({ identity: fetchedIdentity, status: 'pending' });

        const trackFetchUserTime = createPerfLogger('FetchUser');
        // Recorded for `requireFreshFetch` callers. A rejection stays non-fatal here — the flags the
        // client already holds are still the best answer for every other flag. `unavailable` is a
        // service that performs no remote read at all (see `performsRemoteFetch`): its `fetchUser`
        // resolves, but nothing was fetched, so the values it serves are no fresher than before.
        let status: RemoteFeatureFlagFetchStatus = featureFlagService.performsRemoteFetch === false ? 'unavailable' : 'success';
        try {
          const experimentUser = buildExperimentUser(userInfoToFetch);
          await featureFlagService.fetchUser(experimentUser);
          logEvent('FetchUser', { status: 'success', durationMs: trackFetchUserTime(), user: userInfoToFetch });
        } catch (err) {
          status = 'error';
          logEvent('FetchUser', { status: 'error', durationMs: trackFetchUserTime(), user: userInfoToFetch, error: parseError(err) });
        }

        // Only the most recent attempt owns the status; an older one that resolves late is ignored.
        if (attemptId === fetchAttemptIdRef.current) {
          setFetchOutcome({ identity: fetchedIdentity, status });
        }

        logFirstLoadPerf();
      }

      // Only transition to Ready if this is still the latest fetch request.
      // A newer effect invocation may have started a new fetch; avoid setting
      // Ready prematurely for the stale one.
      if (currentFetchId === fetchIdRef.current) {
        setState(RemoteFeatureFlagProviderState.Ready);
      }
    };

    fetchRemoteFeatureFlags();
  }, [authUserId, authHasInitialized, featureFlagService, anonymousId, eventInfoLoading, eventInfo, loadingUserConsent, hasInitialVariants]);

  const { features } = useFeatureFlagsContext();

  const getFeatureFlagValue = useEventCallback(<TFeatureName extends RemoteFeatureFlagKeys>(featureName: TFeatureName, options?: RemoteFeatureFlagOptions) => {
    // 1. Check URL Query Parameter
    // NOTE: useRemoteFeatureFlag ALSO short-circuits on this same override BEFORE the provider
    // finishes loading (it never reaches this function during the Loading window). Once loading
    // settles, that earlier check fires first, so for remote flags this branch is effectively a
    // safety net for non-hook callers. If you change the override semantics here (validation,
    // logging, etc.), mirror the change in useRemoteFeatureFlag's overriddenFeatureValue check.
    const { defaultValue, value: overriddenFeatureValue, supportedValues, key: featureKey, consentValueForTrue } = features[featureName];
    if (overriddenFeatureValue) {
      return { value: overriddenFeatureValue };
    }

    // 2. Check Event-Level User Consent
    const consentKey = options?.consentKey;
    if (consentKey) {
      const { payload } = options.consentFeatureUsesPayload ? featureFlagService.getFeatureValue(featureKey) : { payload: undefined };
      const consentValue = getUserConsent(consentKey);
      if (consentValue === true) {
        const valueForTrue = consentValueForTrue ?? supportedValues[0];
        return { value: valueForTrue, payload };
      } else if (consentValue === false) {
        const fallbackValue = consentValueForTrue ? supportedValues.find(value => value !== consentValueForTrue) : supportedValues[1];
        return { value: fallbackValue, payload };
      }
    }

    // 3. RemoteConfig (Amplitude) Evaluation
    const { payload, value } = featureFlagService.getFeatureValue(featureKey);
    if (value && isSupportedFeatureValue(value, supportedValues)) {
      featureFlagService.trackExposure(featureKey);
      return { payload, value };
    }

    if (value && !isSupportedFeatureValue(value, supportedValues)) {
      logEvent('UnsupportedFeatureValue', { featureName, value, supportedValues, hasPayload: !!payload, payload });
    }

    return { payload, value: defaultValue };
  });

  const loading = state === RemoteFeatureFlagProviderState.Loading;
  // `getFeatureFlagValue` is a stable `useEventCallback`, so this only changes when `loading` or the
  // fetch status flips.
  const contextValue = useMemo(() => ({ loading, fetchStatus, getFeatureFlagValue }), [loading, fetchStatus, getFeatureFlagValue]);

  return <InternalRemoteFeatureFlagsProvider value={contextValue}>{children}</InternalRemoteFeatureFlagsProvider>;
};

export function useRemoteFeatureFlag<TFeatureName extends RemoteFeatureFlagKeys>(featureName?: TFeatureName, options?: RemoteFeatureFlagOptions): RemoteFeatureFlag<TFeatureName> {
  const { loading, fetchStatus, getFeatureFlagValue } = useInternalRemoteFeatureFlagsContext();

  const { features } = useFeatureFlagsContext();
  const defaultFeatureValue = featureName && features[featureName].defaultValue;

  // Use primitive values instead of options reference to avoid re-triggering trackExposure
  // when callers pass inline objects like { consentKey: 'x' } that create new refs every render
  const consentKey = options?.consentKey;
  const skip = options?.skip;
  const consentFeatureUsesPayload = options?.consentFeatureUsesPayload;
  const requireFreshFetch = options?.requireFreshFetch;

  // URL query-param override (`?feature.<name>=<value>`). Resolved here — not only inside
  // getFeatureFlagValue — because overrides must win even while the provider is still Loading.
  // Otherwise a QA override silently reads as the default value until the remote fetch settles
  // (or forever, if a dependency like Segment is blocked), making overrides non-deterministic.
  // KEEP IN SYNC with the override branch at the top of getFeatureFlagValue: once loading
  // settles this check fires first, so changes to override semantics must land in both places.
  const overriddenFeatureValue = featureName ? features[featureName].value : undefined;

  const remoteFeatureFlag = useMemo((): RemoteFeatureFlag<TFeatureName> => {
    if (skip) {
      return {
        loading: false,
        value: defaultFeatureValue,
      };
    }

    if (overriddenFeatureValue) {
      return {
        loading: false,
        value: overriddenFeatureValue as RemoteFeatureFlag<TFeatureName>['value'],
      };
    }

    if (loading || !featureName) {
      return {
        loading: true,
        value: defaultFeatureValue,
      };
    }

    // A kill switch reads the default until this session's own fetch succeeds (JW-136). Below the
    // override branch on purpose: `?feature.x=on` must still win, or the flag can't be tested where
    // the fetch is exactly what's broken. No `trackExposure` either — the user saw the default, and
    // an exposure on a value the flag service never delivered would poison the experiment data.
    if (requireFreshFetch && fetchStatus !== 'success') {
      return {
        loading: fetchStatus === 'pending',
        value: defaultFeatureValue,
      };
    }

    return {
      loading: false,
      ...getFeatureFlagValue(featureName, { consentKey, skip, consentFeatureUsesPayload }),
    };
  }, [loading, fetchStatus, featureName, getFeatureFlagValue, defaultFeatureValue, consentKey, skip, consentFeatureUsesPayload, overriddenFeatureValue, requireFreshFetch]);

  return remoteFeatureFlag;
}

export { InternalRemoteFeatureFlagsContext };
